// src/utils/expenseShared.js
// Shared constants and utilities for all expense pages.
// Single source of truth for categories, CSV parsing, and office normalization.

// ── Category Constants ──────────────────────────────────────────────────

export const OFFICE_OVERHEAD_CATEGORIES = [
  'Energy & Utilities',
  'Equipment & IT: software, hardware',
  'Finance',
  'Fixed Assets Additions',
  'HR',
  'Insurance',
  'Marketing',
  'Materials',
  'Meals & Entertainment',
  'Miscellaneous',
  'Office Operations: supplies, courier, subscriptions',
  'Pension',
  'Premises',
  'Professional Services',
  'Salary',
  'Salary Tax',
  'Travel Costs',
];

// Shared by Project Expenses page, personal reimbursements, and statement import (project rows).
export const PROJECT_EXPENSE_CATEGORIES = [
  'Equipment & IT: software, hardware',
  'Insurance',
  'Marketing',
  'Materials',
  'Meals & Entertainment',
  'Miscellaneous',
  'Office Operations: supplies, courier, subscriptions',
  'Professional Services',
  'Travel Costs',
];

// Mirrors PROJECT_EXPENSE_CATEGORIES — personal expense claims align with project expenses.
export const REIMBURSEMENT_CATEGORIES = [...PROJECT_EXPENSE_CATEGORIES];

// Mirrors OFFICE_OVERHEAD_CATEGORIES — office expense claims use the same taxonomy.
export const OFFICE_EXPENSE_CATEGORIES = [...OFFICE_OVERHEAD_CATEGORIES];

// ── Category canonicalization ───────────────────────────────────────────
// Maps free-text / legacy / misspelled category strings onto the canonical
// OFFICE_OVERHEAD_CATEGORIES value. Used to guard the CSV importer so bad
// category text can't create "orphan" categories — rows whose category isn't
// on the canonical list, which the grouped Office Expenses view historically
// hid from display while still counting them in the totals.
//
// Only HIGH-CONFIDENCE mappings live here: exact/case variants, the legacy
// renames already codified in migration 20260419000001, common misspellings,
// and unambiguous synonyms. Genuinely ambiguous strings (e.g. "IT services")
// deliberately resolve to null so the importer forces a human to choose.
const OFFICE_CATEGORY_ALIASES = {
  // → Equipment & IT: software, hardware
  'equipment': 'Equipment & IT: software, hardware',
  'equipment & it': 'Equipment & IT: software, hardware',
  'equipement & it': 'Equipment & IT: software, hardware',            // misspelling
  'equipement & it: software, hardware': 'Equipment & IT: software, hardware',
  'it equipment': 'Equipment & IT: software, hardware',
  'software': 'Equipment & IT: software, hardware',
  'hardware': 'Equipment & IT: software, hardware',
  // → Office Operations: supplies, courier, subscriptions
  'office operations': 'Office Operations: supplies, courier, subscriptions',
  'office operation': 'Office Operations: supplies, courier, subscriptions',
  'office operation supplies': 'Office Operations: supplies, courier, subscriptions',
  'office operations supplies': 'Office Operations: supplies, courier, subscriptions',
  'office operation stationery supplies': 'Office Operations: supplies, courier, subscriptions',
  'office supplies': 'Office Operations: supplies, courier, subscriptions',
  'stationery': 'Office Operations: supplies, courier, subscriptions',
  'courier': 'Office Operations: supplies, courier, subscriptions',
  'subscriptions': 'Office Operations: supplies, courier, subscriptions',
  // → Professional Services
  'professional': 'Professional Services',
  'professional service': 'Professional Services',
  'consultants': 'Professional Services',
  // → Premises
  'rent': 'Premises',
  // → Energy & Utilities
  'utilities': 'Energy & Utilities',
  // → Travel Costs
  'travel': 'Travel Costs',
  // → Finance
  'bank payment': 'Finance',
  // → Salary Tax
  'tax': 'Salary Tax',
  // → Meals & Entertainment
  'food & beverages': 'Meals & Entertainment',
  // → Miscellaneous
  'misc': 'Miscellaneous',
  'miscellanous': 'Miscellaneous',                                    // misspelling
};

/**
 * Normalize a raw category string to a lookup key: strip surrounding quotes and
 * leading/trailing punctuation (e.g. a stray trailing comma from a bad CSV
 * parse), collapse internal whitespace, and lowercase.
 */
function normalizeCategoryKey(raw) {
  return String(raw ?? '')
    .replace(/["']/g, ' ')
    .replace(/^[\s,.;]+|[\s,.;]+$/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

// Canonical names (case/punctuation-insensitive) + explicit aliases → canonical.
const _CANONICAL_BY_KEY = (() => {
  const map = {};
  for (const c of OFFICE_OVERHEAD_CATEGORIES) map[normalizeCategoryKey(c)] = c;
  for (const [alias, canonical] of Object.entries(OFFICE_CATEGORY_ALIASES)) {
    map[normalizeCategoryKey(alias)] = canonical;
  }
  return map;
})();

/**
 * Resolve a raw office-expense category string to its canonical
 * OFFICE_OVERHEAD_CATEGORIES value, or null when it cannot be confidently
 * mapped (caller should then force a manual choice).
 * @param {string} raw
 * @returns {string|null}
 */
export function canonicalizeOfficeCategory(raw) {
  const key = normalizeCategoryKey(raw);
  if (!key) return null;
  return _CANONICAL_BY_KEY[key] || null;
}

/** True when `cat` is exactly one of the canonical office overhead categories. */
export function isCanonicalOfficeCategory(cat) {
  return OFFICE_OVERHEAD_CATEGORIES.includes(cat);
}

export const REIMBURSEMENT_STATUSES = {
  pending: { label: 'Pending', badge: 'brio-badge--pending' },
  approved: { label: 'Approved', badge: 'brio-badge--warning' },
  reimbursed: { label: 'Reimbursed', badge: 'brio-badge--approved' },
  voucher: { label: 'In Voucher', badge: 'brio-badge--info' },
  invoiced: { label: 'Invoiced', badge: 'brio-badge--accent' },
};

export const OFFICE_EXPENSE_ID = '__office__';

// ── Office Location Normalization ───────────────────────────────────────

export function normalizeOfficeLocation(loc) {
  if (!loc) return 'NL';
  if (loc === 'Rotterdam') return 'NL';
  if (loc === 'Mumbai') return 'IN';
  return loc;
}

// ── CSV Parsing (robust, from ProjectExpensesPage) ──────────────────────

/**
 * Parse a single CSV line respecting RFC 4180 quoted fields.
 */
export function parseCSVLine(line, delimiter) {
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

/**
 * Auto-detect delimiter by counting occurrences in non-quoted portions.
 */
export function detectDelimiter(lines) {
  const sample = lines.slice(0, Math.min(lines.length, 10));
  const candidates = ['\t', ';', ','];
  const scores = candidates.map(delim => {
    let total = 0;
    for (const line of sample) {
      let inQuotes = false;
      let count = 0;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') inQuotes = !inQuotes;
        else if (!inQuotes && line[i] === delim) count++;
      }
      total += count;
    }
    return total;
  });
  const maxIdx = scores.indexOf(Math.max(...scores));
  return candidates[maxIdx] || ',';
}

/**
 * Parse European-format number: "26.437,00" → 26437.00
 */
export function parseEuropeanNumber(str) {
  if (!str) return NaN;
  const s = str.replace(/\s/g, '');
  if (/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(s)) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }
  if (/^\d{1,3}(,\d{2,3})*(\.\d+)?$/.test(s)) {
    return parseFloat(s.replace(/,/g, ''));
  }
  return parseFloat(s);
}

/**
 * Parse various date formats into ISO YYYY-MM-DD.
 */
export function parseDateFlexible(str) {
  if (!str || !str.trim()) return null;
  const s = str.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmy) {
    const [, dd, mm, yyyy] = dmy;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  const months = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const dmmmy = s.match(/^(\d{1,2})[/\-.]([A-Za-z]{3})[/\-.](\d{4})$/);
  if (dmmmy) {
    const [, dd, mmm, yyyy] = dmmmy;
    const mm = months[mmm.toLowerCase()];
    if (mm) return `${yyyy}-${mm}-${dd.padStart(2, '0')}`;
  }
  return null;
}

/**
 * Detect if the CSV is a bank export format (HDFC-style with metadata header rows).
 */
export function detectBankFormat(lines, delimiter) {
  const bankHeaderKeywords = ['utr', 'beneficiary', 'txn'];
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const lower = lines[i].toLowerCase();
    const matchCount = bankHeaderKeywords.filter(kw => lower.includes(kw)).length;
    if (matchCount >= 2) {
      return { isBankFormat: true, headerLine: i, dataStartLine: i + 1 };
    }
  }
  return null;
}

/**
 * Heuristic: does this pasted text look like a raw bank / credit-card statement
 * rather than the office-overhead "Category, Subcategory, Amount" format? Used
 * to steer bank exports to the dedicated Statement Import tool instead of the
 * office CSV importer (whose columns don't line up with a bank export).
 */
export function looksLikeBankStatement(text) {
  const lines = String(text || '').trim().split('\n').filter(l => l.trim());
  if (lines.length === 0) return false;
  const delimiter = lines[0].includes('\t') ? '\t' : ',';
  return !!detectBankFormat(lines, delimiter);
}

/**
 * Build a duplicate-check key from expense fields.
 */
export function expenseDuplicateKey(exp) {
  return `${exp.projectId || ''}|${exp.date}|${exp.amountOriginal}|${(exp.description || '').toLowerCase().trim()}`;
}

/**
 * Simple CSV parser for office overhead format (Category, Subcategory, Amount [, Office] [, Currency]).
 */
export function parseOfficeOverheadCsvRows(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length === 0) return [];
  const sep = lines[0].includes('\t') ? '\t' : ',';

  return lines.map((line, idx) => {
    const cols = line.split(sep).map(c => c.trim());
    if (cols.length < 3) return { error: `Row ${idx + 1}: need at least 3 columns (Category, Subcategory, Amount)`, raw: line };
    const category = cols[0];
    const subcategory = cols[1];
    const amount = parseFloat(cols[2]);
    if (!category) return { error: `Row ${idx + 1}: empty category`, raw: line };
    if (!subcategory) return { error: `Row ${idx + 1}: empty subcategory`, raw: line };
    if (isNaN(amount) || amount <= 0) return { error: `Row ${idx + 1}: invalid amount "${cols[2]}"`, raw: line };
    const office = cols[3] || null;
    const currency = cols[4] || null;
    return { category, subcategory, amount, office, currency, raw: line };
  });
}
